import { useIntl } from 'react-intl';

import { DeviationValue } from '#components/shared/DeviationValue.tsx';
import LabeledValue from '#components/shared/LabeledValue.tsx';
import type { BulkMaterialUnit } from '#domain/model/Goods.ts';
import { hasDeviation } from '#domain/model/hasDeviation.ts';
import { unitOptions } from '#domain/model/unitOptions.ts';

type TourServiceBulkMaterialUnitDetailsProps = {
    unit: BulkMaterialUnit;
};

const sectionClasses = 'display-flex flex-column gap-10 bg-lightest padding-10 rounded';
const columnClasses = 'display-grid gap-10';

export const BulkMaterialUnitDetails = ({ unit }: TourServiceBulkMaterialUnitDetailsProps) => {
    const intl = useIntl();

    const hasLoadingDeviation = hasDeviation(unit.quantity.target.value, unit.quantity.loaded?.value);
    const hasUnloadingDeviation = hasDeviation(unit.quantity.target.value, unit.quantity.unloaded?.value);

    const emptyPlaceholder = '-';
    const loadedQuantity = unit.quantity.loaded;
    const unloadedQuantity = unit.quantity.unloaded;

    const unitLabel = unitOptions.find(u => u.id === unit.quantity.target.type)?.label ?? '';

    return (
        <div data-testid={`order-stop-form-${unit.id}`} className={sectionClasses}>
            <div className={`${columnClasses} grid-cols-1`}>
                <LabeledValue
                    boxed
                    label={intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.material.cargo',
                    })}
                    value={unit.material.name}
                />
            </div>
            <div className={`${columnClasses} grid-cols-3`}>
                <LabeledValue
                    boxed
                    label={intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.material.target-quantity',
                    })}
                    value={`${unit.quantity.target.value} ${unitLabel}`}
                />
                <LabeledValue
                    boxed
                    label={intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.material.quantity-loaded',
                    })}
                    value={
                        hasLoadingDeviation ? (
                            <DeviationValue
                                targetQuantity={unit.quantity.target.value}
                                actualQuantity={loadedQuantity?.value}
                                emptyPlaceholder={emptyPlaceholder}
                                unit={unitLabel}
                            />
                        ) : loadedQuantity !== undefined ? (
                            `${loadedQuantity} ${unitLabel}`
                        ) : (
                            emptyPlaceholder
                        )
                    }
                />
                <LabeledValue
                    boxed
                    label={intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.material.quantity-unloaded',
                    })}
                    value={
                        hasUnloadingDeviation ? (
                            <DeviationValue
                                targetQuantity={unit.quantity.target.value}
                                actualQuantity={unloadedQuantity?.value}
                                emptyPlaceholder={emptyPlaceholder}
                                unit={unitLabel}
                            />
                        ) : unloadedQuantity !== undefined ? (
                            `${unloadedQuantity} ${unitLabel}`
                        ) : (
                            emptyPlaceholder
                        )
                    }
                />
            </div>
            {unit.comments.loading ||
                (unit.comments.unloading && (
                    <div className={`${columnClasses} grid-cols-1 gap-5 padding-x-15`}>
                        {unit.comments.loading && (
                            <LabeledValue
                                label={intl.formatMessage({
                                    id: 'intl-msg:ordercom-cloud-frontend.orders.details.stop.form.input.comment.driver.loading',
                                })}
                                icon='rioglyph rioglyph-comment'
                                value={unit.comments.loading}
                            />
                        )}
                        {unit.comments.unloading && (
                            <LabeledValue
                                label={intl.formatMessage({
                                    id: 'intl-msg:ordercom-cloud-frontend.orders.details.stop.form.input.comment.driver.unloading',
                                })}
                                icon='rioglyph rioglyph-comment'
                                value={unit.comments.unloading}
                            />
                        )}
                    </div>
                ))}
        </div>
    );
};
